/**
 * The recovery ladder (user-management §2.5, screen A8), its P1 rows:
 * - a recovery code and a new password: in at once, or after a 72-hour hold
 *   when another device was used in the last 30 days (the server decides);
 * - approval from a device where you're signed in: this page waits and
 *   continues by itself once that device approves;
 * - none of these: the account can't be recovered (said plainly).
 * A held request is remembered on this device (its id and recovery token)
 * so you can come back and complete it; any signed-in device can cancel it.
 * Server contract: `POST /auth/recovery/start` then `/complete`, which
 * answers RECOVERY_NOT_READY with `nx_recovery_state` until it may finish.
 */
import {
  ApiErrorCode,
  type AuthSessionResponse,
  type PowSolution,
  type RecoveryPath,
  type RecoveryStartRequest,
} from '@neosocial/protocol';
import { useEffect, useState, type FormEvent } from 'react';
import { useT } from '../i18n';
import { api, ApiRequestError, isApiRequestError, NetworkError } from '../lib/api';
import { solvePow } from '../lib/pow';
import { storage } from '../lib/storage';
import { startSession } from '../store/session';
import { Btn, Icon } from '../ui/kit';
import { toast } from '../ui/overlay';
import { clientInfo } from './clientInfo';
import { isLeakedPassword } from './leaked';
import { PasswordInput } from './PasswordInput';
import { autoFocusOk } from './keyboard';

const SAVED_KEY = 'recovery-request';
/** How often the waiting page asks whether a device approved (approvals last 15 minutes). */
export const CLAIM_POLL_MS = 5000;
/** How often it asks during a 72-hour hold. */
export const HOLD_POLL_MS = 60_000;

interface Saved {
  id: string;
  /** The recovery token: only this device can complete the request. */
  secret: string;
  method: RecoveryPath;
  notBefore: string | null;
}

export function savedRecovery(): Saved | null {
  return storage.getJson<Saved | null>(SAVED_KEY, null);
}

type View =
  | { v: 'choose' }
  | { v: 'code' }
  | { v: 'lost' }
  | { v: 'waiting'; saved: Saved }
  | { v: 'ended'; key: string };

async function startWithPow(b: RecoveryStartRequest) {
  try {
    return await api.um.recoveryStart(b);
  } catch (e) {
    if (!isApiRequestError(e, ApiErrorCode.POW_REQUIRED)) throw e;
  }
  const c = await api.auth.powChallenge({ purpose: 'login' });
  const pow: PowSolution = await solvePow(c);
  return api.um.recoveryStart({ ...b, pow });
}

function errorText(e: unknown, t: (k: string, v?: Record<string, string | number>) => string) {
  if (e instanceof NetworkError) return t('ns.err.network');
  if (e instanceof ApiRequestError) {
    if (e.status === 429) return t('auth.signin.wait', { n: Math.ceil(e.retryAfter ?? 30) });
    // the new password: leaked, or too short or long
    if (isLeakedPassword(e, 'new_password')) return t('auth.password.breached');
    if (e.fieldCodes().new_password) return t('auth.password.help');
    const key = `ns.err.${e.code}`;
    const s = t(key);
    if (s !== key) return s;
  }
  return t('ns.err.generic');
}

/** Completing signs every other session out (the server does it). */
function signedIn(r: AuthSessionResponse, t: (k: string) => string) {
  storage.remove(SAVED_KEY);
  startSession(r);
  toast(t('um.recovery.done'), { icon: 'shield-check', tone: 'ok', duration: 9000 });
}

export function Recovery({ back, forgot }: { back(): void; forgot?: () => void }) {
  const { t, fmt } = useT();
  const [view, setView] = useState<View>(() => {
    const s = savedRecovery();
    return s ? { v: 'waiting', saved: s } : { v: 'choose' };
  });
  const [login, setLogin] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // the waiting page asks until the request is approved, its hold ends, or it ends
  const waiting = view.v === 'waiting' ? view.saved : null;
  useEffect(() => {
    if (!waiting) return;
    let live = true;
    let timer = 0;
    const tick = async () => {
      try {
        const r = await api.um.recoveryComplete({
          request_id: waiting.id,
          recovery_token: waiting.secret,
          client: clientInfo(),
        });
        if (!live) return;
        signedIn(r, t);
        return;
      } catch (e) {
        if (!live) return;
        const ended =
          isApiRequestError(e, ApiErrorCode.RECOVERY_NOT_READY) &&
          e.body?.nx_recovery_state !== undefined &&
          e.body.nx_recovery_state !== 'pending';
        // a wrong id or token (another device's request) also ends it here
        if (ended || isApiRequestError(e, ApiErrorCode.INVALID_CREDENTIALS)) {
          storage.remove(SAVED_KEY);
          setView({
            v: 'ended',
            key:
              isApiRequestError(e) && e.body?.nx_recovery_state === 'cancelled'
                ? 'um.recovery.cancelled'
                : 'um.recovery.expired',
          });
          return;
        }
      }
      timer = window.setTimeout(
        () => void tick(),
        waiting.notBefore ? HOLD_POLL_MS : CLAIM_POLL_MS,
      );
    };
    void tick();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [waiting, t]);

  const start = async (method: 'code' | 'device', e?: FormEvent) => {
    e?.preventDefault();
    if (!login.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await startWithPow({
        login: login.trim(),
        path: method,
        ...(method === 'code' ? { recovery_code: code.trim(), new_password: password } : {}),
        // this device, as sign-in names it (Settings › Devices, the approval prompt)
        client: clientInfo(),
      });
      if (r.state === 'approved') {
        // no hold applies: finish at once
        signedIn(
          await api.um.recoveryComplete({
            request_id: r.request_id,
            recovery_token: r.recovery_token,
            // the session is made here: without this it showed as "Unknown app"
            client: clientInfo(),
          }),
          t,
        );
        return;
      }
      const saved: Saved = {
        id: r.request_id,
        secret: r.recovery_token,
        method,
        notBefore: r.not_before,
      };
      storage.setJson(SAVED_KEY, saved);
      setView({ v: 'waiting', saved });
    } catch (er) {
      setErr(errorText(er, t));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The server lets only a signed-in device cancel a request; this device can
   * only stop waiting for it here (the request keeps running until it ends).
   */
  const forget = () => {
    storage.remove(SAVED_KEY);
    setView({ v: 'choose' });
  };

  const userField = (
    <label className="ns-field">
      {/* what sign-in takes: the email, or a legacy account's username */}
      <span className="ns-label">{t('auth.email.label')}</span>
      <input
        className="nx-input"
        dir="ltr"
        inputMode="email"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck={false}
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        data-autofocus
        autoFocus={autoFocusOk()}
      />
      <span className="ns-help">{t('auth.signin.loginHelp')}</span>
    </label>
  );
  const errLine = err ? (
    <p className="ns-err ns-form-err" role="alert">
      {err}
    </p>
  ) : null;
  const backLink = (
    <p className="ns-auth-alt">
      <button type="button" className="ns-link" onClick={back}>
        {t('c.back')}
      </button>
    </p>
  );

  if (view.v === 'waiting') {
    const s = view.saved;
    return (
      <div className="ns-auth-form" role="status" aria-live="polite">
        <span className="ns-auth-ic">
          <Icon name={s.notBefore ? 'hourglass' : 'monitor'} />
        </span>
        <h1>{t('recovery.title')}</h1>
        {s.notBefore ? (
          <p className="ns-auth-sub">
            {t('recovery.wait', { date: fmt.date(s.notBefore), time: fmt.time(s.notBefore) })}
          </p>
        ) : (
          <>
            <p className="ns-auth-sub">
              {t('um.recovery.waitingDevice', { brand: t('ns.brand') })}
            </p>
            <p className="ns-help">
              <i className="nx-spin" /> {t('um.recovery.stillWaiting')}
            </p>
          </>
        )}
        {errLine}
        <p className="ns-help">{t('um.recovery.cancelElsewhere')}</p>
        <Btn kind="ghost" className="ns-wide" onClick={forget}>
          {t('um.recovery.forget')}
        </Btn>
        {backLink}
      </div>
    );
  }
  if (view.v === 'ended') {
    return (
      <div className="ns-auth-form" role="status">
        <span className="ns-auth-ic">
          <Icon name="info" />
        </span>
        <h1>{t('recovery.title')}</h1>
        <p className="ns-auth-sub">{t(view.key)}</p>
        <Btn kind="primary" className="ns-wide" onClick={() => setView({ v: 'choose' })}>
          {t('um.recovery.send')}
        </Btn>
        {backLink}
      </div>
    );
  }
  if (view.v === 'lost') {
    return (
      <div className="ns-auth-form" role="alert">
        <span className="ns-auth-ic">
          <Icon name="alert" />
        </span>
        <h1>{t('recovery.title')}</h1>
        <p className="ns-auth-sub">{t('recovery.lost')}</p>
        {forgot ? (
          <Btn kind="primary" className="ns-wide" icon="mail" onClick={forgot}>
            {t('auth.forgot.link')}
          </Btn>
        ) : null}
        <p className="ns-auth-alt">
          <button type="button" className="ns-link" onClick={() => setView({ v: 'choose' })}>
            {t('c.back')}
          </button>
        </p>
      </div>
    );
  }
  if (view.v === 'code') {
    return (
      <form className="ns-auth-form" onSubmit={(e) => void start('code', e)} noValidate>
        <h1>{t('recovery.code')}</h1>
        {userField}
        <label className="ns-field">
          <span className="ns-label">{t('um.recovery.codeLabel')}</span>
          <input
            className="nx-input"
            dir="ltr"
            autoComplete="one-time-code"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('um.recovery.newPassword')}</span>
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="ns-help">{t('auth.password.help')}</span>
        </label>
        {errLine}
        <Btn
          kind="primary"
          type="submit"
          className="ns-wide"
          busy={busy}
          disabled={busy || !login.trim() || !code.trim() || password.length < 8}
        >
          {t('um.recovery.send')}
        </Btn>
        <p className="ns-auth-alt">
          <button type="button" className="ns-link" onClick={() => setView({ v: 'choose' })}>
            {t('c.back')}
          </button>
        </p>
      </form>
    );
  }
  return (
    <div className="ns-auth-form">
      <h1>{t('recovery.title')}</h1>
      {userField}
      <div className="ns-recovery-paths">
        <Btn kind="ghost" className="ns-wide" icon="shield" onClick={() => setView({ v: 'code' })}>
          {t('recovery.code')}
        </Btn>
        <Btn
          kind="ghost"
          className="ns-wide"
          icon="monitor"
          busy={busy}
          disabled={busy || !login.trim()}
          onClick={() => void start('device')}
        >
          {t('recovery.device')}
        </Btn>
      </div>
      {errLine}
      <p className="ns-auth-alt">
        <button type="button" className="ns-link" onClick={() => setView({ v: 'lost' })}>
          {t('um.recovery.lostLink')}
        </button>
      </p>
      {backLink}
    </div>
  );
}
